import { Prompt } from '../types';
import { PROMPTS_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const usePromptsApi = () => {
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getPrompts = async (getAllPrompts: boolean = false): Promise<Prompt[]> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${PROMPTS_URL}?getAllPrompts=${getAllPrompts}`,
        {
          method: 'GET',
          headers
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch Prompts');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching Prompts:', error);
      throw error;
    }
  };

  const getPrompt = async (id: string): Promise<Prompt> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PROMPTS_URL}/${id}`,
        {
          method: 'GET',
          headers
        }
      );
      console.log('[getPrompt] Response:', response);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch Prompt';
        let errorDetails;
        
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching Prompt:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const createPrompt = async (prompt: Omit<Prompt, 'id'>): Promise<Prompt> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PROMPTS_URL}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(prompt)
        }
      );

      if (!response.ok) {
        console.log('[createPrompt] Error creating Prompt response:', response);
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          console.log('[createPrompt] Error creating Prompt errorData:', errorData);
          throw new Error(errorData.message || 'Failed to create Prompt');
        } catch (parseError) {
          console.log('[createPrompt] Error parsing JSON:', String(parseError));
          console.log('[createPrompt] Error parsing JSON Type:', typeof (parseError));
          throw new Error(String(parseError));
        }
      }
      return response.json();
    } catch (error) {
      console.error('Error creating Prompt:', error);
      throw error;
    }
  };

  const updatePrompt = async (prompt: Partial<Prompt>): Promise<Prompt> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PROMPTS_URL}/${prompt.promptId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(prompt)
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update Prompt');
      }
      return response.json();
    } catch (error) {
      console.error('Error updating Prompt:', error);
      throw error;
    }
  };

  const deletePrompt = async (id: string): Promise<void> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PROMPTS_URL}/${id}`,
        {
          method: 'DELETE',
          headers
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete Prompt');
      }
      return;
    } catch (error) {
      console.error('Error deleting Prompt:', error);
      throw error;
    }
  };

  return {
    getPrompts,
    getPrompt,
    createPrompt,
    updatePrompt,
    deletePrompt
  };
}